import React, { useState, useEffect, useCallback } from 'react';
import monitoringService from '../services/monitoringService';
import { Card } from '../components/common';

const Monitoring = () => {
  const [activeTab, setActiveTab] = useState('ssl');
  const [sslData, setSslData] = useState(null);
  const [uptimeData, setUptimeData] = useState(null);
  const [diskData, setDiskData] = useState(null);
  const [dbData, setDbData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ssl, uptime, disk, dbHistory] = await Promise.all([
        monitoringService.getSSLCertificates(),
        monitoringService.getUptime(24),
        monitoringService.getDiskHistory(30),
        monitoringService.getDbHistory(30)
      ]);
      if (ssl.success) setSslData(ssl.data);
      if (uptime.success) setUptimeData(uptime.data);
      if (disk.success) setDiskData(disk.data);
      if (dbHistory.success) setDbData(dbHistory.data);
    } catch (err) {
      console.error('Monitoring load error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const tabs = [
    { id: 'ssl', label: 'SSL 인증서', icon: '🔒' },
    { id: 'uptime', label: '업타임', icon: '📡' },
    { id: 'disk', label: '디스크', icon: '💾' },
    { id: 'db', label: '데이터베이스', icon: '🗄️' }
  ];

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <p className="text-gray-600 text-sm">모니터링 데이터 로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-[28px] font-bold text-gray-900 m-0">모니터링</h1>
        <button onClick={loadData} className="text-sm text-blue-500 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-lg">새로고침</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-[1px] ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* SSL Tab */}
      {activeTab === 'ssl' && sslData && (
        <div className="flex flex-col gap-4">
          {sslData.warnings > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
              ⚠️ {sslData.warnings}개 도메인의 SSL 인증서가 30일 이내 만료됩니다.
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">도메인</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">발급자</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">만료일</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">남은 일수</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">상태</th>
                </tr>
              </thead>
              <tbody>
                {sslData.certificates.map((cert, i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-0">
                    <td className="py-3 px-4 font-medium text-gray-900">{cert.domain}</td>
                    <td className="py-3 px-4 text-gray-600">{cert.issuer || '-'}</td>
                    <td className="py-3 px-4 text-gray-600">{cert.validTo ? new Date(cert.validTo).toLocaleDateString('ko-KR') : '-'}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-semibold ${
                        cert.daysRemaining > 30 ? 'text-green-600' :
                        cert.daysRemaining > 7 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {cert.daysRemaining !== undefined ? `${cert.daysRemaining}일` : '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 py-1 px-2.5 rounded-full text-xs font-medium ${
                        cert.status === 'ok' ? 'bg-green-100 text-green-700' :
                        cert.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                        cert.status === 'critical' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {cert.status === 'ok' ? '정상' : cert.status === 'warning' ? '주의' : cert.status === 'critical' ? '위험' : cert.error || '오류'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Uptime Tab */}
      {activeTab === 'uptime' && uptimeData && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3">
            {uptimeData.services.map((service, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-medium text-gray-900 text-sm">{service.service_name}</span>
                    <span className={`text-xs font-semibold py-0.5 px-2 rounded-full ${
                      service.uptime_percentage >= 99 ? 'bg-green-100 text-green-700' :
                      service.uptime_percentage >= 95 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {service.uptime_percentage}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        service.uptime_percentage >= 99 ? 'bg-green-500' :
                        service.uptime_percentage >= 95 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(service.uptime_percentage, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>응답: {service.avg_response_time}ms</div>
                  <div>{service.total_checks}회 체크</div>
                </div>
              </div>
            ))}
          </div>
          {uptimeData.downEvents.length > 0 && (
            <Card title="최근 다운타임 이벤트">
              <div className="space-y-2">
                {uptimeData.downEvents.map((event, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-red-50 rounded-lg text-sm">
                    <span className="font-medium text-red-800">{event.service_name}</span>
                    <span className="text-red-600 text-xs">{event.error_message}</span>
                    <span className="text-gray-500 text-xs">{new Date(event.checked_at).toLocaleString('ko-KR')}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Disk Tab */}
      {activeTab === 'disk' && (
        <div className="flex flex-col gap-4">
          {diskData && diskData.length > 0 ? (
            <Card title="디스크 사용량 추이 (30일)">
              <div className="space-y-3">
                {diskData.slice(-10).map((entry, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="text-xs text-gray-500 w-[140px]">{new Date(entry.recorded_at).toLocaleString('ko-KR')}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${entry.percentage > 80 ? 'bg-red-500' : entry.percentage > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${entry.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700 w-[60px] text-right">{entry.percentage}%</span>
                    <span className="text-xs text-gray-500 w-[100px] text-right">{formatBytes(entry.used_bytes)} / {formatBytes(entry.total_bytes)}</span>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <div className="text-center py-12 text-gray-500 text-sm">
              디스크 사용량 데이터가 아직 수집되지 않았습니다. 1시간 후 데이터가 표시됩니다.
            </div>
          )}
        </div>
      )}

      {/* DB Tab */}
      {activeTab === 'db' && (
        <div className="flex flex-col gap-4">
          {dbData && dbData.length > 0 ? (
            <Card title="DB 사이즈 추이">
              <div className="bg-white rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">DB</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">크기</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">기록 시간</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbData.slice(-20).map((entry, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 px-4 font-medium text-gray-900">{entry.db_name}</td>
                        <td className="py-3 px-4 text-right text-gray-700">{formatBytes(entry.file_size)}</td>
                        <td className="py-3 px-4 text-right text-gray-500 text-xs">{new Date(entry.recorded_at).toLocaleString('ko-KR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <div className="text-center py-12 text-gray-500 text-sm">
              DB 사이즈 데이터가 아직 수집되지 않았습니다. 6시간 후 데이터가 표시됩니다.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Monitoring;
